import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { publicApi } from '../services/publicApi';

// Tenant Context
const TenantContext = createContext()

// Tenant Provider Component
export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Prevent double loading in React Strict Mode
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    loadTenantData();
  }, []);

  // Paint the theme from the tenant's own colours. The stylesheet ships with
  // the founding federation's red and blue; every tenant overrides them here,
  // so a rebrand is a settings change rather than a build.
  useEffect(() => {
    if (!siteSettings) return;

    const root = document.documentElement;
    const primary = siteSettings.primary_color;
    const secondary = siteSettings.secondary_color;

    if (primary) {
      root.style.setProperty('--tb-primary-color', primary);
      root.style.setProperty('--primary-color', primary);
    }
    if (secondary) {
      root.style.setProperty('--tb-secondary-color', secondary);
      root.style.setProperty('--secondary-color', secondary);
    }

    // The top bar defaults to the tricolour, which suits a national federation
    // and nothing else. A tenant that sets its own also stops the wave.
    if (siteSettings.topbar_background) {
      root.style.setProperty('--tb-topbar-background', siteSettings.topbar_background);
    }
    // The bar drifts across its gradient by default. Only a tenant that asks
    // for stillness gets it.
    if (siteSettings.topbar_animated === false) {
      root.style.setProperty('--tb-topbar-animation', 'none');
    }
    // The name and affiliations sit on that bar, so they follow it.
    if (siteSettings.topbar_color) {
      root.style.setProperty('--tb-topbar-color', siteSettings.topbar_color);
    }
  }, [siteSettings]);

  const loadTenantData = async () => {
    console.log('TenantProvider: Starting to load tenant data...');
    setLoading(true);
    setError(null);

    try {
      console.log('TenantProvider: Making API calls in parallel...');

      // Load both API calls in parallel using Promise.allSettled to prevent flicker
      const [homepageResponse, settingsResponse] = await Promise.allSettled([
        publicApi.getHomepage(),
        publicApi.getSiteSettings()
      ]);

      // Handle homepage/tenant data
      if (homepageResponse.status === 'fulfilled') {
        console.log('TenantProvider: Homepage response:', homepageResponse.value.data);

        if (homepageResponse.value.data.data && homepageResponse.value.data.data.tenant) {
          console.log('TenantProvider: Setting tenant from data.data:', homepageResponse.value.data.data.tenant);
          setTenant(homepageResponse.value.data.data.tenant);
        } else if (homepageResponse.value.data.tenant) {
          console.log('TenantProvider: Setting tenant from data:', homepageResponse.value.data.tenant);
          setTenant(homepageResponse.value.data.tenant);
        }
      } else {
        throw new Error(homepageResponse.reason?.message || 'Failed to load tenant data');
      }

      // Handle settings data
      if (settingsResponse.status === 'fulfilled') {
        console.log('TenantProvider: Settings response:', settingsResponse.value.data);

        if (settingsResponse.value.data.data) {
          setSiteSettings(settingsResponse.value.data.data);
        } else if (settingsResponse.value.data) {
          setSiteSettings(settingsResponse.value.data);
        }
      } else {
        console.warn('TenantProvider: Failed to load site settings (non-critical):', settingsResponse.reason?.message);
        // Set default site settings
        const tenantName = homepageResponse.value?.data?.data?.tenant?.name ||
                          homepageResponse.value?.data?.tenant?.name ||
                          'Baseball Federation';
        setSiteSettings({
          site_title: tenantName,
          site_description: `Official website of ${tenantName}`,
          primary_color: '#1976d2',
          secondary_color: '#dc004e'
        });
      }

      console.log('TenantProvider: All data loaded successfully');
    } catch (error) {
      console.error('TenantProvider: Failed to load tenant data:', error);
      console.error('TenantProvider: Error details:', error.response?.data);
      setError(error.message || 'Failed to load tenant information');
    } finally {
      // Set loading to false only after BOTH API calls are complete
      setLoading(false);
    }
  };

  const updateTenant = (updates) => {
    setTenant(prevTenant => ({
      ...prevTenant,
      ...updates
    }));
  };

  const updateSiteSettings = (newSettings) => {
    setSiteSettings(prevSettings => ({
      ...prevSettings,
      ...newSettings
    }));
  };

  const refreshTenant = () => {
    loadTenantData();
  };

  // Browser tab title and icon follow the tenant, so a tenant's site does not
  // sit under the platform's own name once its settings have loaded.
  useEffect(() => {
    const name = siteSettings?.siteName || siteSettings?.site_title || tenant?.name;
    if (name) document.title = name;

    const icon = siteSettings?.favicon || siteSettings?.logo;
    if (icon) {
      let link = document.querySelector('link[rel="icon"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = icon;
    }
  }, [siteSettings, tenant]);

  // Context value
  const value = {
    tenant,
    siteSettings,
    loading,
    error,
    updateTenant,
    updateSiteSettings,
    refreshTenant,
    tenantSlug: tenant?.slug,
    tenantDomain: tenant?.domain || tenant?.subdomain,
    isCustomDomain: !!tenant?.domain,
    settings: siteSettings || {}
  };

  // Show simple loader while tenant data is being fetched
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f9fafb'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '5px solid #e5e7eb',
          borderTop: '5px solid #022a5e',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }}></div>
        <p style={{
          marginTop: '1rem',
          color: '#6b7280',
          fontSize: '0.95rem',
          fontWeight: '500'
        }}>Loading...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    )
  }

  // Don't render children if tenant loading failed
  if (error && !tenant) {
    return (
      <div className="tenant-error">
        <div className="error-container">
          <h1>Tenant Not Found</h1>
          <p>The requested organization could not be found.</p>
          <button onClick={refreshTenant}>Retry</button>
        </div>
      </div>
    )
  }

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  )
}

// Custom hook to use tenant context
export const useTenant = () => {
  const context = useContext(TenantContext)
  
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  
  return context
}

// Hook for tenant-specific theming
export const useTenantTheme = () => {
  const { settings } = useTenant()
  
  const theme = settings?.theme || {}
  
  return {
    primaryColor: theme.primaryColor || '#1976d2',
    secondaryColor: theme.secondaryColor || '#dc004e',
    fontFamily: theme.fontFamily || 'Roboto, sans-serif',
    logo: settings?.logo,
    favicon: settings?.favicon,
    siteName: settings?.siteName || 'Baseball Federation',
    
    // Apply theme to document
    applyTheme: () => {
      const root = document.documentElement
      root.style.setProperty('--primary-color', theme.primaryColor || '#1976d2')
      root.style.setProperty('--secondary-color', theme.secondaryColor || '#dc004e')
      root.style.setProperty('--font-family', theme.fontFamily || 'Roboto, sans-serif')
      
      // Update favicon if available
      if (settings?.favicon) {
        const favicon = document.querySelector('link[rel="icon"]')
        if (favicon) {
          favicon.href = settings.favicon
        } else {
          const newFavicon = document.createElement('link')
          newFavicon.rel = 'icon'
          newFavicon.href = settings.favicon
          document.head.appendChild(newFavicon)
        }
      }
      
      // Update page title
      if (settings?.siteName) {
        document.title = settings.siteName
      }
    }
  }
}

// Hook for tenant-specific settings
export const useTenantSettings = () => {
  const { settings, updateTenant } = useTenant()
  
  const updateSettings = (newSettings) => {
    updateTenant({
      settings: {
        ...settings,
        ...newSettings
      }
    })
  }
  
  return {
    settings: settings || {},
    updateSettings,
    
    // Specific setting getters
    getContactInfo: () => ({
      email: settings?.contactEmail,
      phone: settings?.contactPhone,
      address: settings?.address
    }),
    
    getSocialMedia: () => settings?.socialMedia || {},
    
    getTheme: () => settings?.theme || {},
    
    getSiteInfo: () => ({
      name: settings?.siteName,
      description: settings?.siteDescription,
      logo: settings?.logo,
      favicon: settings?.favicon
    })
  }
}